import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    Home, TrendingUp, Users, Euro, CheckCircle2, XCircle, ChevronRight,
    Save, Download, Filter, RefreshCw, ShieldAlert, X, Search,
} from "lucide-react";

const SUB_TABS = [
    { id: "stats", label: "Statistiche" },
    { id: "prices", label: "Prezzi Pacchetti" },
    { id: "payments", label: "Pagamenti" },
    { id: "leads", label: "Lead & Riassegnazione" },
];

const STATUS_LABELS = {
    nuovo: "Nuovo",
    assegnato: "Assegnato",
    contattato: "Contattato",
    in_trattativa: "In trattativa",
    chiuso: "Chiuso",
    perso: "Perso",
};

export default function AdminOwnerLeads() {
    const [sub, setSub] = useState("stats");

    return (
        <div data-testid="admin-owner-leads" className="space-y-6">
            <div>
                <span className="label-eyebrow text-gold">Super Admin · Gestione Lead</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2 flex items-center gap-2">
                    <Home size={22} className="text-gold" /> Lead Proprietari
                </h2>
                <p className="text-ink-400 text-sm mt-1">Statistiche complete, gestione prezzi pacchetti, storico pagamenti e riassegnazioni manuali.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-2">
                {SUB_TABS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`adm-ol-tab-${t.id}`}
                        onClick={() => setSub(t.id)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${sub === t.id ? "text-gold border-b-2 border-gold" : "text-ink-400 hover:text-ink-100"}`}
                    >{t.label}</button>
                ))}
            </div>

            {sub === "stats" && <StatsPanel />}
            {sub === "prices" && <PricesPanel />}
            {sub === "payments" && <PaymentsPanel />}
            {sub === "leads" && <LeadsPanel />}
        </div>
    );
}

// ---------------------------------------------------------------
// STATISTICHE
// ---------------------------------------------------------------
function StatsPanel() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/owner-leads/stats?days=${days}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [days]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {[7, 30, 90, 365].map((d) => (
                        <button
                            key={d}
                            data-testid={`adm-ol-period-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-3 py-1.5 text-xs border ${days === d ? "border-gold text-gold" : "border-white/15 text-ink-400 hover:border-white/30"}`}
                        >Ultimi {d}g</button>
                    ))}
                </div>
                <a
                    data-testid="adm-ol-stats-csv"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/owner-leads/stats.csv?days=${days}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-white/15 hover:border-gold hover:text-gold text-ink-100"
                ><Download size={12} /> Export CSV</a>
            </div>

            {loading ? (
                <div className="text-ink-400 text-sm py-12 text-center">Caricamento…</div>
            ) : !data ? (
                <div className="text-ink-400 text-sm py-12 text-center">Impossibile caricare statistiche</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Kpi icon={<Users size={12} />} label="Lead totali" value={data.totals.leads} testid="adm-kpi-total" />
                        <Kpi icon={<CheckCircle2 size={12} />} label="Assegnati" value={data.totals.assigned} testid="adm-kpi-assigned" />
                        <Kpi icon={<TrendingUp size={12} />} label="Tasso accettazione" value={`${data.acceptance_rate}%`} testid="adm-kpi-acceptance" />
                        <Kpi icon={<Euro size={12} />} label="Fatturato pacchetti" value={`€${(data.revenue.gross_eur || 0).toFixed(2)}`} sub={`${data.revenue.transactions} pagamenti · ${data.revenue.credits_sold} crediti venduti`} testid="adm-kpi-revenue" isPrice />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="border border-white/10 p-5 bg-ink-900/40">
                            <h3 className="text-sm text-gold uppercase tracking-wider mb-4">Distribuzione per stato</h3>
                            <div className="space-y-2">
                                {Object.entries(data.counts_by_status).map(([k, v]) => (
                                    <div key={k} className="flex items-center gap-3 text-sm">
                                        <div className="w-28 text-ink-400">{STATUS_LABELS[k] || k}</div>
                                        <div className="flex-1 bg-black/40 h-2 relative">
                                            <div className="bg-gold h-2" style={{ width: `${data.totals.leads ? (v / data.totals.leads) * 100 : 0}%` }} />
                                        </div>
                                        <div className="w-10 text-right text-ink-100 tabular-nums">{v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border border-white/10 p-5 bg-ink-900/40">
                            <h3 className="text-sm text-gold uppercase tracking-wider mb-4">Top agenzie</h3>
                            {data.top_agencies.length === 0 ? (
                                <div className="text-ink-400 text-sm py-6 text-center">Nessuna agenzia ha ricevuto lead nel periodo</div>
                            ) : (
                                <div className="divide-y divide-white/5">
                                    {data.top_agencies.slice(0, 8).map((a) => (
                                        <div key={a.agency_id} className="py-2.5 flex items-center gap-3 text-sm">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-ink-100 truncate">{a.agency_name}</div>
                                                <div className="text-[10px] text-ink-500 truncate">{a.email}</div>
                                            </div>
                                            <div className="text-xs text-ink-400 tabular-nums">{a.total} lead · {a.accepted}✓ · {a.lost}✗</div>
                                            <div className="w-14 text-right text-gold tabular-nums">{a.acceptance_rate}%</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function Kpi({ icon, label, value, sub, testid, isPrice }) {
    return (
        <div data-testid={testid} className="border border-white/10 p-5 bg-ink-900/40">
            <div className="flex items-center gap-2 text-xs text-gold tracking-[0.2em] uppercase">{icon} {label}</div>
            <div className={`font-serif text-3xl mt-3 ${isPrice ? "text-price" : "text-ink-50"}`}>{value}</div>
            {sub && <div className="text-[10px] text-ink-400 mt-2">{sub}</div>}
        </div>
    );
}

// ---------------------------------------------------------------
// PREZZI PACCHETTI
// ---------------------------------------------------------------
function PricesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [ok, setOk] = useState(false);
    const [err, setErr] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/admin/owner-leads/packages")
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const update = (i, k, v) => {
        const next = [...items];
        next[i] = { ...next[i], [k]: v };
        setItems(next);
    };

    const save = async () => {
        setSaving(true); setErr(""); setOk(false);
        try {
            const payload = { items: items.map((it) => ({ ...it, quantity: Number(it.quantity), price: Number(it.price) })) };
            await api.put("/admin/owner-leads/packages", payload);
            setOk(true); load();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento…</div>;

    return (
        <div className="space-y-4">
            <div className="border border-amber-700/40 bg-amber-900/10 p-4 text-xs text-amber-200 flex items-start gap-2">
                <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
                <div>Le modifiche entrano in vigore immediatamente. Prezzi IVA 22% inclusa. La sorgente attiva è mostrata come <b>db</b> quando presente, altrimenti <b>default</b>.</div>
            </div>
            {ok && <div data-testid="prices-saved" className="border border-green-700/40 bg-green-900/20 text-green-300 text-sm p-3 flex items-center gap-2"><CheckCircle2 size={14} /> Prezzi aggiornati</div>}
            {err && <div className="border border-red-700/40 bg-red-900/20 text-red-300 text-sm p-3">{err}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((it, i) => (
                    <div key={it.id} data-testid={`price-card-${it.id}`} className="border border-white/10 bg-ink-900/40 p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-400">ID</div>
                                <div className="font-mono text-sm text-ink-100">{it.id}</div>
                            </div>
                            <span className={`text-[10px] uppercase px-2 py-0.5 ${it.source === "db" ? "bg-gold/20 text-gold" : "bg-white/5 text-ink-400"}`}>{it.source}</span>
                        </div>
                        <Row label="Etichetta">
                            <input data-testid={`inp-label-${it.id}`} value={it.label || ""} onChange={(e) => update(i, "label", e.target.value)} className="inp" />
                        </Row>
                        <div className="grid grid-cols-2 gap-3">
                            <Row label="Quantità lead">
                                <input data-testid={`inp-qty-${it.id}`} type="number" min="1" value={it.quantity} onChange={(e) => update(i, "quantity", e.target.value)} className="inp" />
                            </Row>
                            <Row label="Prezzo (€)">
                                <input data-testid={`inp-price-${it.id}`} type="number" min="0.01" step="0.01" value={it.price} onChange={(e) => update(i, "price", e.target.value)} className="inp" />
                            </Row>
                        </div>
                        <Row label="Descrizione unitaria">
                            <input value={it.unit_hint || ""} onChange={(e) => update(i, "unit_hint", e.target.value)} className="inp" />
                        </Row>
                        <Row label="Badge (facoltativo)">
                            <input data-testid={`inp-badge-${it.id}`} value={it.badge || ""} placeholder="Es. Più scelto, Miglior valore" onChange={(e) => update(i, "badge", e.target.value)} className="inp" />
                        </Row>
                        <label className="flex items-center gap-2 text-sm text-ink-100 mt-2">
                            <input type="checkbox" checked={!!it.active} onChange={(e) => update(i, "active", e.target.checked)} />
                            Attivo
                        </label>
                    </div>
                ))}
            </div>

            <div className="flex justify-end">
                <button
                    data-testid="prices-save-btn"
                    disabled={saving}
                    onClick={save}
                    className="cta-gold px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
                ><Save size={13} /> {saving ? "Salvataggio…" : "Salva prezzi"}</button>
            </div>

            <style>{`.inp{width:100%;background:rgba(0,0,0,0.4);border:1px solid rgba(255,255,255,0.12);color:#e6e6e6;padding:8px 10px;font-size:12px;outline:none}.inp:focus{border-color:#B08A3E}`}</style>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-1">{label}</div>
            {children}
        </div>
    );
}

// ---------------------------------------------------------------
// PAGAMENTI
// ---------------------------------------------------------------
function PaymentsPanel() {
    const [days, setDays] = useState(90);
    const [status, setStatus] = useState("");
    const [data, setData] = useState({ items: [], count: 0, gross_paid: 0 });
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (days) params.set("days", days);
        if (status) params.set("status", status);
        api.get(`/admin/owner-leads/payments?${params}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ items: [], count: 0, gross_paid: 0 }))
            .finally(() => setLoading(false));
    };
    useEffect(load, [days, status]);

    const csvUrl = useMemo(() => {
        const params = new URLSearchParams();
        if (days) params.set("days", days);
        if (status) params.set("status", status);
        return `${process.env.REACT_APP_BACKEND_URL}/api/admin/owner-leads/payments.csv?${params}`;
    }, [days, status]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Filter size={12} className="text-ink-400" />
                    <select data-testid="pay-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-black/40 border border-white/15 text-ink-100 text-xs px-2 py-1.5">
                        <option value="7">Ultimi 7g</option>
                        <option value="30">Ultimi 30g</option>
                        <option value="90">Ultimi 90g</option>
                        <option value="365">Ultimo anno</option>
                        <option value="730">Ultimi 2 anni</option>
                    </select>
                    <select data-testid="pay-status" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-black/40 border border-white/15 text-ink-100 text-xs px-2 py-1.5">
                        <option value="">Tutti gli stati</option>
                        <option value="paid">Pagati</option>
                        <option value="pending">In attesa</option>
                        <option value="expired">Scaduti</option>
                    </select>
                    <button onClick={load} className="p-1.5 text-ink-400 hover:text-gold"><RefreshCw size={12} /></button>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-xs text-ink-400">Fatturato periodo: <span className="text-price font-serif text-lg">€{data.gross_paid.toFixed(2)}</span></div>
                    <a data-testid="pay-csv" href={csvUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-white/15 hover:border-gold hover:text-gold text-ink-100"><Download size={12} /> CSV</a>
                </div>
            </div>

            <div className="border border-white/10">
                <div className="grid grid-cols-12 gap-2 p-3 border-b border-white/10 text-[10px] uppercase tracking-wider text-ink-400">
                    <div className="col-span-2">Data</div>
                    <div className="col-span-3">Agenzia</div>
                    <div className="col-span-3">Pacchetto</div>
                    <div className="col-span-1 text-right">Qta</div>
                    <div className="col-span-1 text-right">€</div>
                    <div className="col-span-2 text-right">Stato</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-ink-400 text-sm">Caricamento…</div>
                ) : data.items.length === 0 ? (
                    <div className="p-8 text-center text-ink-400 text-sm">Nessun pagamento nel periodo selezionato</div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {data.items.map((p) => (
                            <div key={p.session_id} data-testid={`pay-row-${p.session_id}`} className="grid grid-cols-12 gap-2 p-3 text-sm hover:bg-white/5">
                                <div className="col-span-2 text-ink-400 text-xs">{(p.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                <div className="col-span-3 text-ink-100 truncate">
                                    <div>{p.agency_label}</div>
                                    <div className="text-[10px] text-ink-500 truncate">{p.user_email}</div>
                                </div>
                                <div className="col-span-3 text-ink-100 truncate">{p.package_label || p.package_id}</div>
                                <div className="col-span-1 text-right text-ink-100">{p.package_quantity}</div>
                                <div className="col-span-1 text-right text-price tabular-nums">{(Number(p.amount || 0)).toFixed(2)}</div>
                                <div className="col-span-2 text-right">
                                    <span className={`text-[10px] uppercase px-2 py-0.5 ${p.payment_status === "paid" ? "bg-green-900/40 text-green-300" : "bg-white/5 text-ink-400"}`}>{p.payment_status || "?"}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------
// LEAD + RIASSEGNAZIONE MANUALE
// ---------------------------------------------------------------
function LeadsPanel() {
    const [filters, setFilters] = useState({ days: 90, status: "", city: "" });
    const [data, setData] = useState({ items: [], count: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [reassignLead, setReassignLead] = useState(null);

    const load = () => {
        setLoading(true);
        setError("");
        const params = new URLSearchParams();
        if (filters.days) params.set("days", filters.days);
        if (filters.status) params.set("status", filters.status);
        if (filters.city) params.set("city", filters.city);
        api.get(`/admin/owner-leads/leads?${params}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                setError(e?.response?.data?.detail || "Errore caricamento lead");
                setData({ items: [], count: 0 });
            })
            .finally(() => setLoading(false));
    };
    useEffect(load, [filters]);

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="lead-days" value={filters.days} onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })} className="bg-black/40 border border-white/15 text-ink-100 text-xs px-2 py-1.5">
                    <option value="30">30 giorni</option>
                    <option value="90">90 giorni</option>
                    <option value="365">1 anno</option>
                </select>
                <select data-testid="lead-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="bg-black/40 border border-white/15 text-ink-100 text-xs px-2 py-1.5">
                    <option value="">Tutti gli stati</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
                </select>
                <div className="relative">
                    <Search size={12} className="absolute left-2 top-2 text-ink-400" />
                    <input data-testid="lead-city" placeholder="Città…" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="bg-black/40 border border-white/15 text-ink-100 text-xs pl-7 pr-2 py-1.5" />
                </div>
                <div className="text-xs text-ink-400 ml-auto">{data.count} risultati</div>
            </div>

            <div className="border border-white/10">
                <div className="grid grid-cols-12 gap-2 p-3 border-b border-white/10 text-[10px] uppercase tracking-wider text-ink-400">
                    <div className="col-span-2">Data</div>
                    <div className="col-span-3">Cliente</div>
                    <div className="col-span-2">Città</div>
                    <div className="col-span-2">Agenzia</div>
                    <div className="col-span-2">Stato</div>
                    <div className="col-span-1 text-right">Azioni</div>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-ink-400 text-sm">Caricamento…</div>
                ) : error ? (
                    <div data-testid="adm-lead-error" className="p-8 text-center text-red-300 text-sm">{error}</div>
                ) : data.items.length === 0 ? (
                    <div className="p-8 text-center text-ink-400 text-sm">Nessun lead trovato</div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {data.items.map((l) => (
                            <div key={l.id} data-testid={`adm-lead-row-${l.id}`} className="grid grid-cols-12 gap-2 p-3 text-sm hover:bg-white/5 items-center">
                                <div className="col-span-2 text-ink-400 text-xs">{(l.created_at || "").slice(0, 10)}</div>
                                <div className="col-span-3 truncate">
                                    <div className="text-ink-100">{l.first_name} {l.last_name}</div>
                                    <div className="text-[10px] text-ink-500 truncate">{l.email} · {l.objective}</div>
                                </div>
                                <div className="col-span-2 text-ink-100">{l.city}</div>
                                <div className="col-span-2 text-ink-100 text-xs truncate">{l.agency_label || <span className="text-ink-500">— non assegnato —</span>}</div>
                                <div className="col-span-2"><span className={`text-[10px] uppercase px-2 py-0.5 ${statusChip(l.status)}`}>{STATUS_LABELS[l.status] || l.status}</span></div>
                                <div className="col-span-1 text-right">
                                    <button data-testid={`adm-lead-reassign-${l.id}`} onClick={() => setReassignLead(l)} className="text-xs text-gold hover:underline">Riassegna</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {reassignLead && <ReassignModal lead={reassignLead} onClose={() => setReassignLead(null)} onDone={() => { setReassignLead(null); load(); }} />}
        </div>
    );
}

function statusChip(s) {
    return {
        nuovo: "bg-blue-900/40 text-blue-300",
        assegnato: "bg-amber-900/40 text-amber-300",
        contattato: "bg-cyan-900/40 text-cyan-300",
        in_trattativa: "bg-purple-900/40 text-purple-300",
        chiuso: "bg-green-900/40 text-green-300",
        perso: "bg-red-900/40 text-red-300",
    }[s] || "bg-white/5 text-ink-400";
}

function ReassignModal({ lead, onClose, onDone }) {
    const [agencies, setAgencies] = useState([]);
    const [selected, setSelected] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        api.get("/admin/users", { params: { q: "", plan: "agenzia" } })
            .then((r) => {
                // Solo agenzie con piano Agenzia Full (€129) realmente attivo
                const list = (r.data.items || r.data || []).filter(
                    (u) => u.role === "agenzia" && u.plan_id === "agenzia" && u.plan_active === true
                );
                setAgencies(list);
            })
            .catch(() => setAgencies([]));
    }, []);

    const filtered = agencies.filter((a) => {
        const s = search.toLowerCase();
        if (!s) return true;
        const zonesLc = (a.zone_operative || []).map((z) => String(z).toLowerCase());
        return (
            (a.agency_name || "").toLowerCase().includes(s)
            || (a.email || "").toLowerCase().includes(s)
            || (a.name || "").toLowerCase().includes(s)
            || zonesLc.some((z) => z.includes(s))
        );
    }).slice(0, 25);

    const submit = async () => {
        if (!selected) { setErr("Seleziona un'agenzia"); return; }
        setSaving(true); setErr("");
        try {
            await api.post(`/admin/owner-leads/${lead.id}/reassign`, { new_agency_id: selected, reason });
            onDone();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore riassegnazione");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="reassign-modal" className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-ink-950 border border-white/10 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <span className="label-eyebrow text-gold">Riassegnazione manuale</span>
                        <h3 className="font-serif text-xl text-ink-50 mt-1">{lead.first_name} {lead.last_name} · {lead.city}</h3>
                        <p className="text-xs text-ink-400 mt-1">Attuale: <b className="text-ink-100">{lead.agency_label || "non assegnato"}</b></p>
                    </div>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-4">
                    {err && <div className="border border-red-700/40 bg-red-900/20 text-red-300 text-sm p-3">{err}</div>}
                    <p className="text-[10px] uppercase tracking-wider text-ink-500">Solo agenzie con piano <b className="text-gold">Agenzia Full (€129)</b> attivo</p>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-ink-400">Cerca agenzia</label>
                        <input data-testid="reassign-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, email, zona operativa…" className="w-full bg-black/40 border border-white/15 text-ink-100 text-sm px-3 py-2 mt-1" />
                    </div>
                    <div className="border border-white/10 max-h-64 overflow-y-auto divide-y divide-white/5">
                        {filtered.map((a) => (
                            <label key={a.user_id} className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5 ${selected === a.user_id ? "bg-gold/10" : ""}`}>
                                <input type="radio" name="agy" checked={selected === a.user_id} onChange={() => setSelected(a.user_id)} data-testid={`reassign-radio-${a.user_id}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink-100 truncate">{a.agency_name || a.name || a.email}</div>
                                    <div className="text-[10px] text-ink-500 truncate">{a.email}</div>
                                    {Array.isArray(a.zone_operative) && a.zone_operative.length > 0 ? (
                                        <div data-testid={`reassign-zones-${a.user_id}`} className="flex flex-wrap gap-1 mt-1.5">
                                            {a.zone_operative.slice(0, 6).map((z, i) => (
                                                <span key={i} className="text-[9px] uppercase tracking-wider bg-gold/10 border border-gold/30 text-gold px-1.5 py-0.5">{z}</span>
                                            ))}
                                            {a.zone_operative.length > 6 && (
                                                <span className="text-[9px] text-ink-500 px-1 py-0.5">+{a.zone_operative.length - 6}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <div data-testid={`reassign-zones-empty-${a.user_id}`} className="text-[9px] uppercase tracking-wider text-red-400/70 mt-1.5">Nessuna zona operativa</div>
                                    )}
                                </div>
                                <span className="text-[10px] uppercase tracking-wider text-gold/80">Agenzia Full</span>
                            </label>
                        ))}
                        {filtered.length === 0 && <div className="p-4 text-center text-ink-400 text-sm">Nessuna agenzia trovata</div>}
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-ink-400">Motivo (facoltativo)</label>
                        <textarea data-testid="reassign-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full bg-black/40 border border-white/15 text-ink-100 text-sm px-3 py-2 mt-1" placeholder="Es. agenzia originale inattiva, richiesta specifica del cliente…" />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="cta-outline px-5 py-2 text-sm">Annulla</button>
                        <button data-testid="reassign-confirm" disabled={saving || !selected} onClick={submit} className="cta-gold px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
                            {saving ? "Salvo…" : (<><ChevronRight size={12} /> Riassegna</>)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
